import {
  PageableDto,
  PageOfAdmission,
  Admission,
} from "@reside/reside-api-admission";
import {useInfiniteQuery, useQueryClient} from "react-query";
import {useClearAndCancelCache} from "../../../hooks/useClearAndCancelCache";
import {createConfig} from "../../../routers/useApiClientConfiguration";

import {getAuthorizationHeader} from "../../../models/admin-session/selectors";
import {ViewMode} from "../constants";
import {admissionsApi} from "../../../services/AdmissionsService";
import {AUTH_ERROR_STATUS} from "../../../services/api";

const ADMISSIONS_BY_STATUS_QUERY_KEY = "admissionsByStatus";

type Params = {
  facilityIds: ReadonlyArray<string> | null;
  status?: Admission.StatusEnum;
  sortBy?: string;
  sortDirection?: PageableDto.DirectionEnum;
  /** Newly created user without a facility is not allowed to see admissions. */
  isAllowedToSeeAdmissions?: boolean;
  starredOnly?: boolean;
  /** View mode: grid vs list source */
  viewMode?: ViewMode;
  workflowType?: string | null;
};

const defaultSortParams = {
  sortBy: "updated",
  sortDirection: PageableDto.DirectionEnum.DESC,
};

export const useAdmissionsByStatusQuery = ({
  facilityIds,
  status,
  sortBy = defaultSortParams.sortBy,
  sortDirection = defaultSortParams.sortDirection,
  pageSize = 10,
  isAllowedToSeeAdmissions,
  starredOnly,
  viewMode,
  workflowType,
}: Params & {
  pageSize?: number;
}) => {
  const {clearAndCancelCache} = useClearAndCancelCache();
  const config = createConfig(clearAndCancelCache);

  // Don't run query if facilities haven't been loaded yet (null)
  // If isAllowedToSeeAdmissions is provided, also check that permission
  const facilitiesLoaded = facilityIds !== null;
  const shouldEnableQuery =
    facilitiesLoaded &&
    (isAllowedToSeeAdmissions === undefined || isAllowedToSeeAdmissions);

  return useInfiniteQuery(
    [
      ADMISSIONS_BY_STATUS_QUERY_KEY,
      {
        facilityIds,
        status,
        sortBy,
        sortDirection,
        starredOnly,
        viewMode,
        workflowType,
      },
    ],
    async ({pageParam = 0}) => {
      const {data} = await admissionsApi.searchAdmissionUsingPOST(
        {
          facilityIds,
          statuses: status ? [status as any] : undefined,
          pageable: {
            page: pageParam,
            size: pageSize,
            properties: [sortBy],
            direction: sortDirection,
          },
          starredOnly,
          workflowType: workflowType ?? undefined,
        } as any,
        getAuthorizationHeader(),
        {query: viewMode ? {viewMode} : undefined},
      );
      return data;
    },
    {
      getNextPageParam: ({content, totalPages}, allPages) => {
        const page = allPages.length;
        const isLastPage = totalPages === page;

        return content.length === pageSize && !isLastPage ? page : false;
      },
      cacheTime: 0,
      refetchOnMount: false,
      onError: async (err: Response) => {
        // Temporary untill all admissions endpoints are rewriten in python
        if (
          err.status === AUTH_ERROR_STATUS.FORBIDDEN ||
          err.status === AUTH_ERROR_STATUS.UNAUTHORIZED
        ) {
          config.logout();
        }
      },
      enabled: shouldEnableQuery,
      onSuccess: data => {
        const lastPage = data.pages[data.pages.length - 1];
        if (
          data.pages.length > 0 &&
          lastPage?.totalStarredAdmissions !== undefined
        ) {
          sessionStorage.setItem(
            "totalStarredAdmissions",
            lastPage.totalStarredAdmissions.toString(),
          );
          // Dispatch event to notify other components
          window.dispatchEvent(new Event("starredAdmissionsUpdated"));
        }
      },
    },
  );
};

export const useAdmissionsByStatusQueryUtils = () => {
  const cache = useQueryClient();

  return {
    refetchAllAdmissionsByStatusQueries: () =>
      cache.refetchQueries([ADMISSIONS_BY_STATUS_QUERY_KEY]),
    refetchAdmissionsByStatusQuery: (params: Params) => {
      if (params.viewMode) {
        return cache.refetchQueries([
          ADMISSIONS_BY_STATUS_QUERY_KEY,
          {...defaultSortParams, ...params},
        ]);
      }
      cache.refetchQueries([
        ADMISSIONS_BY_STATUS_QUERY_KEY,
        {...defaultSortParams, ...params, viewMode: "grid" as const},
      ]);
      return cache.refetchQueries([
        ADMISSIONS_BY_STATUS_QUERY_KEY,
        {...defaultSortParams, ...params, viewMode: "list" as const},
      ]);
    },
    getAdmissionsByStatusQueryData: (params: Params) => {
      const keyBase = {...defaultSortParams, ...params};
      if (params.viewMode) {
        return cache.getQueryData<{pages: PageOfAdmission[]}>([
          ADMISSIONS_BY_STATUS_QUERY_KEY,
          keyBase,
        ]);
      }
      const gridData = cache.getQueryData<{pages: PageOfAdmission[]}>([
        ADMISSIONS_BY_STATUS_QUERY_KEY,
        {...keyBase, viewMode: "grid" as const},
      ]);
      if (gridData) return gridData;
      return cache.getQueryData<{pages: PageOfAdmission[]}>([
        ADMISSIONS_BY_STATUS_QUERY_KEY,
        {...keyBase, viewMode: "list" as const},
      ]);
    },
    setAdmissionsByStatusQueryData: (params: Params, updater: any) => {
      const keyBase = {...defaultSortParams, ...params};
      if (params.viewMode) {
        return cache.setQueryData(
          [ADMISSIONS_BY_STATUS_QUERY_KEY, keyBase],
          updater,
        );
      }
      cache.setQueryData(
        [
          ADMISSIONS_BY_STATUS_QUERY_KEY,
          {...keyBase, viewMode: "grid" as const},
        ],
        updater,
      );
      return cache.setQueryData(
        [
          ADMISSIONS_BY_STATUS_QUERY_KEY,
          {...keyBase, viewMode: "list" as const},
        ],
        updater,
      );
    },
  };
};
